import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { TelemetryPoint, Finding, Severity } from '../types';
import { calculateAssetGeometry, E3_HARDWARE_H_FOV, E3_HARDWARE_V_FOV, E3_EFFECTIVE_FOV_MULTIPLIER, calculateSurfaceFootprint, E3_CAMERA_WALL_OFFSET } from '../services/telemetryService';

interface TrajectoryMapTProps {
  points: TelemetryPoint[];
  findings?: Finding[];
  onClose: () => void;
  range?: [number, number];
  modelPoints?: TelemetryPoint[];
  previewMode?: 'MODEL' | 'PATH' | 'BOTH';
  mapMode?: 'ANALYSIS' | 'PLANNING';
  minimal?: boolean;
  currentTime?: number;
  baseTimestamp?: number;
  targetOverlap?: number;
  forceShowCaptureLocations?: boolean;
  showCapOnly?: boolean;
  hideFrustrums?: boolean;
  overrideGeometry?: {
    radius: number;
    baseZ: number;
    coneHeight: number;
    cylinderHeight: number;
    center: { x: number, y: number };
    coneBottomRadius?: number;
  };
  initialCloudOffset?: { x: number, y: number };
}

const TAN_H = Math.tan(((E3_HARDWARE_H_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) / 2) * (Math.PI / 180));
const TAN_V = Math.tan(((E3_HARDWARE_V_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) / 2) * (Math.PI / 180));

const applyQuaternion = (p: { x: number, y: number, z: number }, q: { x: number, y: number, z: number, w: number }) => {
  const { x, y, z } = p, { x: qx, y: qy, z: qz, w: qw } = q;
  const tx = 2 * (qy * z - qz * y), ty = 2 * (qz * x - qx * z), tz = 2 * (qx * y - qy * x);
  return { x: x + qw * tx + (qy * tz - qz * ty), y: y + qw * ty + (qz * tx - qx * tz), z: z + qw * tz + (qx * ty - qy * tx) };
};

const severityColors: Record<Severity, string> = {
  [Severity.NONE]: '#94a3b8',
  [Severity.LOW]: '#38bdf8',
  [Severity.MEDIUM]: '#fbbf24',
  [Severity.HIGH]: '#fb923c',
  [Severity.CRITICAL]: '#f87171',
};

export const TrajectoryMapT: React.FC<TrajectoryMapTProps> = ({
  points, findings = [], onClose, range, modelPoints = [], previewMode = 'BOTH', mapMode = 'ANALYSIS', minimal = false, currentTime, baseTimestamp, targetOverlap, forceShowCaptureLocations, showCapOnly, hideFrustrums, overrideGeometry, initialCloudOffset
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [viewAngle, setViewAngle] = useState({ pitch: 90, yaw: 0 }); // Default to upright Front-ish
  const [zoom, setZoom] = useState(1.0);
  const [focusCoord, setFocusCoord] = useState<{ x: number, y: number, z: number } | null>(null);
  const [interactionMode, setInteractionMode] = useState<'ORBIT' | 'PAN'>('ORBIT');

  const [hasInitializedView, setHasInitializedView] = useState(false);
  const [pointDensity, setPointDensity] = useState(50); // 1-100, higher = more points
  const cloudOffset = initialCloudOffset || { x: 0, y: 0 };

  const velocityRef = useRef({ pitch: 0, yaw: 0 });
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const requestRef = useRef<number | null>(null);

  const logStart = useMemo(() => points.length > 0 ? points[0].timestamp : 0, [points]);
  const displayTime = currentTime !== undefined ? currentTime : 0;
  const floorZ = useMemo(() => points.length > 0 ? points[0].z : 0, [points]);

  const assetGeometry = useMemo(() => {
    if (overrideGeometry) return { centerX: overrideGeometry.center.x, centerY: overrideGeometry.center.y, radius: overrideGeometry.radius, diameter: overrideGeometry.radius * 2, minZ: overrideGeometry.baseZ, maxZ: overrideGeometry.baseZ + overrideGeometry.coneHeight + overrideGeometry.cylinderHeight, totalHeight: overrideGeometry.coneHeight + overrideGeometry.cylinderHeight, validPoints: modelPoints || [], coneBottomRadius: overrideGeometry.coneBottomRadius ?? 0.5, coneHeight: overrideGeometry.coneHeight, cylinderHeight: overrideGeometry.cylinderHeight, groundZ: overrideGeometry.baseZ };
    const geom = calculateAssetGeometry(points);
    return { ...geom, centerX: geom.center.x, centerY: geom.center.y, diameter: geom.radius * 2, groundZ: floorZ };
  }, [points, overrideGeometry, floorZ, modelPoints]);

  const getLocalWallRadius = useCallback((z: number) => {
    const { minZ, radius, coneHeight, coneBottomRadius } = assetGeometry, relZ = z - minZ;
    if (coneHeight && relZ < coneHeight) return (coneBottomRadius || 0.5) + (radius - (coneBottomRadius || 0.5)) * Math.max(0, relZ / coneHeight);
    return radius;
  }, [assetGeometry]);

  const getOpticalDistanceToCloud = useCallback((pos: { x: number, y: number, z: number }, rot: { x: number, y: number, z: number, w: number }, defaultRadius: number) => {
    if (!modelPoints || modelPoints.length === 0) {
      const dx = pos.x - assetGeometry.centerX, dy = pos.y - assetGeometry.centerY;
      return Math.abs(defaultRadius - Math.sqrt(dx * dx + dy * dy));
    }

    const vLook = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
    let minDist = 5.0;
    let found = false;

    const step = Math.max(1, Math.ceil(modelPoints.length / 1000));
    for (let i = 0; i < modelPoints.length; i += step) {
      const p = modelPoints[i];
      const dx = p.x - pos.x, dy = p.y - pos.y, dz = p.z - pos.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d > 5.0 || d < 0.1) continue;

      const dot = (dx * vLook.x + dy * vLook.y + dz * vLook.z) / d;
      if (dot > 0.95) {
        if (!found || d < minDist) {
          minDist = d;
          found = true;
        }
      }
    }

    return found ? minDist : 1.5;
  }, [modelPoints, assetGeometry]);

  const captureIndices = useMemo(() => {
    if (!points || points.length === 0) return [];
    let lastIdx = 0, indices: number[] = [0];
    const strideScale = Math.max(0.01, 1 - ((targetOverlap ?? 25) / 100));

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      const pPrev = points[lastIdx];
      if (p.forceCapture) { indices.push(i); lastIdx = i; continue; }

      const standoff = getOpticalDistanceToCloud({ x: p.x, y: p.y, z: p.z }, { x: p.qx, y: p.qy, z: p.qz, w: p.qw }, assetGeometry.radius);
      const frameW = 2 * Math.max(0.1, standoff - E3_CAMERA_WALL_OFFSET) * TAN_H;
      const frameH = 2 * Math.max(0.1, standoff - E3_CAMERA_WALL_OFFSET) * TAN_V;

      const dx = p.x - pPrev.x, dy = p.y - pPrev.y, dz = p.z - pPrev.z, totalDist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const dominantStride = Math.abs(dz) > Math.sqrt(dx * dx + dy * dy) ? frameH : frameW;

      if (totalDist >= dominantStride * strideScale) { indices.push(i); lastIdx = i; }
    }
    return indices;
  }, [points, assetGeometry, targetOverlap, getOpticalDistanceToCloud]);

  const activeCaptureIndex = useMemo(() => {
    if (displayTime === undefined || captureIndices.length === 0) return -1;
    let bestIdx = 0; const tBase = baseTimestamp !== undefined ? baseTimestamp : logStart;
    for (let i = 0; i < captureIndices.length; i++) { if (points[captureIndices[i]].timestamp - tBase <= displayTime + 0.05) bestIdx = i; else break; }
    return bestIdx;
  }, [displayTime, captureIndices, points, logStart, baseTimestamp]);

  const activeFootprint = useMemo(() => {
    if (activeCaptureIndex === -1 || points.length === 0) return null;
    const p = points[captureIndices[activeCaptureIndex]];
    const rot = { x: p.qx, y: p.qy, z: p.qz, w: p.qw };

    const realStandoff = getOpticalDistanceToCloud({ x: p.x, y: p.y, z: p.z }, rot, assetGeometry.radius);
    const opticalStandoff = Math.max(0.01, realStandoff - E3_CAMERA_WALL_OFFSET);

    return {
      standoff: opticalStandoff,
      ...calculateSurfaceFootprint(opticalStandoff, assetGeometry.radius),
      standoffCm: Math.round(opticalStandoff * 100),
      centerStandoffCm: Math.round(realStandoff * 100)
    };
  }, [activeCaptureIndex, captureIndices, points, assetGeometry, getOpticalDistanceToCloud]);

  const viewFocus = useMemo(() => ({ x: assetGeometry.centerX, y: assetGeometry.centerY, z: (assetGeometry.minZ + assetGeometry.maxZ) / 2 }), [assetGeometry]);
  const zeroHeading = useMemo(() => points.length > 0 ? Math.atan2(points[0].y - assetGeometry.centerY, points[0].x - assetGeometry.centerX) : 0, [points, assetGeometry]);

  const setView = useCallback((type: 'Top' | 'Front' | 'Side') => {
    setFocusCoord(null); const zeroDeg = (zeroHeading * 180 / Math.PI);
    if (type === 'Top') { setViewAngle({ pitch: 180, yaw: 90 - zeroDeg }); setZoom(1.4); }
    else if (type === 'Front') { setViewAngle({ pitch: 90, yaw: 90 - zeroDeg }); setZoom(1.6); }
    else if (type === 'Side') { setViewAngle({ pitch: 90, yaw: -zeroDeg }); setZoom(1.6); }
  }, [zeroHeading]);

  useEffect(() => {
    if (!hasInitializedView && points.length > 2) {
      // Use a brief timeout to ensure asset geometry is stable for heading calculation
      const timer = setTimeout(() => {
        setView('Side');
        setHasInitializedView(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [points, setView, hasInitializedView]);



  const drawMap = useCallback((
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    angle: { pitch: number, yaw: number },
    zoomVal: number,
    focus: { x: number, y: number, z: number },
    options: {
      hideFrustrums?: boolean,
      showPath?: boolean,
      showModel?: boolean,
      minimal?: boolean,
      showGuides?: boolean
    } = {}
  ) => {
    const { hideFrustrums = false, showPath = true, showModel = true, minimal: isMin = false, showGuides = false } = options;
    ctx.fillStyle = '#020617'; ctx.fillRect(0, 0, width, height);
    const baseScale = Math.min(width, height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoomVal;
    const rY = (angle.yaw * Math.PI) / 180, rP = (angle.pitch * Math.PI) / 180, cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.sin(rP), midX = width / 2, midY = height / 2;

    const project = (px: number, py: number, pz: number) => {
      const dx = px - focus.x, dy = py - focus.y, dz = pz - focus.z;
      const nx = -dx * baseScale, ny = -dy * baseScale, nz = dz * baseScale;
      const rx = nx * cosY - ny * sinY, ry1 = nx * sinY + ny * cosY, ry = ry1 * cosP - nz * sinP, rz = ry1 * sinP + nz * cosP;
      return { x: midX + rx, y: midY + ry, rz };
    };

    const drawFrustum = (pos: { x: number, y: number, z: number }, rot: { x: number, y: number, z: number, w: number }, color: string, isSolid: boolean = false, thick: number = 1) => {
      const vLook = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
      const opticalL = Math.min(2.5, getOpticalDistanceToCloud(pos, rot, assetGeometry.radius));
      const hw = opticalL * TAN_H, hh = opticalL * TAN_V, cameraOffset = applyQuaternion({ x: E3_CAMERA_WALL_OFFSET, y: 0, z: 0 }, rot);
      const camX = pos.x + cameraOffset.x, camY = pos.y + cameraOffset.y, camZ = pos.z + cameraOffset.z;
      const verts = [{ x: 0, y: 0, z: 0 }, { x: opticalL, y: -hw, z: -hh }, { x: opticalL, y: hw, z: -hh }, { x: opticalL, y: hw, z: hh }, { x: opticalL, y: -hw, z: hh }].map(v => { const r = applyQuaternion(v, rot); return project(camX + r.x, camY + r.y, camZ + r.z); });

      ctx.strokeStyle = 'rgba(245, 158, 11, 0.5)'; ctx.lineWidth = 0.5; ctx.beginPath(); for (let i = 1; i <= 4; i++) { ctx.moveTo(verts[0].x, verts[0].y); ctx.lineTo(verts[i].x, verts[i].y); } ctx.stroke();
      ctx.strokeStyle = color; ctx.lineWidth = isSolid ? thick * 1.5 : thick; ctx.beginPath(); ctx.moveTo(verts[1].x, verts[1].y); ctx.lineTo(verts[2].x, verts[2].y); ctx.lineTo(verts[3].x, verts[3].y); ctx.lineTo(verts[4].x, verts[4].y); ctx.lineTo(verts[1].x, verts[1].y); ctx.stroke();
      if (isSolid) { ctx.globalAlpha = 0.2; ctx.fillStyle = color; ctx.fill(); ctx.globalAlpha = 1.0; }
    };

    // Ground Plane
    if (points.length > 0 || overrideGeometry) {
      const { centerX, centerY, radius } = assetGeometry, groundSize = radius * 4.5, corners = [{ x: centerX - groundSize, y: centerY - groundSize }, { x: centerX + groundSize, y: centerY - groundSize }, { x: centerX + groundSize, y: centerY + groundSize }, { x: centerX - groundSize, y: centerY + groundSize }].map(c => project(c.x, c.y, floorZ));
      ctx.beginPath(); ctx.moveTo(corners[0].x, corners[0].y); ctx.lineTo(corners[1].x, corners[1].y); ctx.lineTo(corners[2].x, corners[2].y); ctx.lineTo(corners[3].x, corners[3].y); ctx.closePath(); ctx.fillStyle = 'rgba(6, 182, 212, 0.05)'; ctx.fill();
    }

    // Model Points
    if ((previewMode === 'BOTH' || previewMode === 'MODEL') && modelPoints && modelPoints.length > 0) {
      const densityFactor = Math.max(1, Math.ceil((100 - pointDensity) / 10));
      const skip = isDraggingRef.current ? Math.max(densityFactor, Math.ceil(modelPoints.length / 20000)) : densityFactor;
      ctx.beginPath(); ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      for (let i = 0; i < modelPoints.length; i += skip) { const p = modelPoints[i], pr = project(p.x + cloudOffset.x, p.y + cloudOffset.y, p.z); ctx.rect(pr.x - 0.5, pr.y - 0.5, 1, 1); } ctx.fill();
    }

    // Findings Visualization
    if (findings && findings.length > 0) {
      findings.forEach((f) => {
        if (!f.spatialCoords) return;
        const pr = project(f.spatialCoords.x, f.spatialCoords.y, f.spatialCoords.z);
        const color = severityColors[f.severity] || '#f97316';
        ctx.fillStyle = color; ctx.beginPath(); ctx.arc(pr.x, pr.y, 4, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.beginPath(); ctx.arc(pr.x, pr.y, 8 + Math.sin(Date.now() / 200) * 2, 0, Math.PI * 2); ctx.stroke();
        if (!minimal) { ctx.fillStyle = '#fff'; ctx.font = 'bold 9px JetBrains Mono'; ctx.fillText(f.type.toUpperCase(), pr.x + 10, pr.y); }
      });
    }

    // Flight Path
    if (points.length > 0 && previewMode !== 'MODEL') { ctx.beginPath(); ctx.lineWidth = 2.0; ctx.strokeStyle = 'rgba(6, 182, 212, 0.8)'; points.forEach((p, i) => { const pr = project(p.x, p.y, p.z); if (i === 0) ctx.moveTo(pr.x, pr.y); else ctx.lineTo(pr.x, pr.y); }); ctx.stroke(); }

    // Frustums
    if (!hideFrustrums) {
      captureIndices.forEach((idx, cIdx) => {
        const p = points[idx], rot = { x: p.qx, y: p.qy, z: p.qz, w: p.qw }, isAct = activeCaptureIndex === cIdx;
        if (showCapOnly && cIdx !== activeCaptureIndex && cIdx !== activeCaptureIndex - 1) return;
        if (range && (p.timestamp - (baseTimestamp || logStart) < range[0] || p.timestamp - (baseTimestamp || logStart) > range[1])) return;
        const color = isAct ? 'rgba(34, 197, 94, 0.9)' : 'rgba(250, 204, 21, 0.5)';
        drawFrustum({ x: p.x, y: p.y, z: p.z }, rot, color, isAct, isAct ? 2 : 1);
      });
    }

    // UAV Avatar
    if (displayTime !== undefined && points.length > 0) {
      const tT = displayTime + (baseTimestamp !== undefined ? baseTimestamp : logStart);
      let p1 = points[0], p2 = points[0], lerp = 0;
      if (points.length > 1) {
        let l = 0, h = points.length - 1;
        if (tT >= points[h].timestamp) p1 = points[h], p2 = points[h];
        else { while (l <= h) { const m = (l + h) >>> 1; if (points[m].timestamp <= tT) l = m + 1; else h = m - 1; } const idx = Math.max(0, l - 1); p1 = points[idx]; p2 = points[idx + 1] || points[idx]; const span = p2.timestamp - p1.timestamp; if (span > 0.001) lerp = (tT - p1.timestamp) / span; }
      }
      const rawPos = { x: p1.x + (p2.x - p1.x) * lerp, y: p1.y + (p2.y - p1.y) * lerp, z: p1.z + (p2.z - p1.z) * lerp };
      const uav = project(rawPos.x, rawPos.y, rawPos.z);
      ctx.fillStyle = '#fff'; ctx.shadowBlur = 10; ctx.shadowColor = '#06b6d4'; ctx.beginPath(); ctx.arc(uav.x, uav.y, 5, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
    }

    // Guides (Radius Circle & Height Gauge)
    if (showGuides) {
      const segments = 64; ctx.beginPath(); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      for (let i = 0; i <= segments; i++) {
        const theta = (i / segments) * Math.PI * 2, px = assetGeometry.centerX + assetGeometry.radius * Math.cos(theta), py = assetGeometry.centerY + assetGeometry.radius * Math.sin(theta), pr = project(px, py, focus.z);
        if (i === 0) ctx.moveTo(pr.x, pr.y); else ctx.lineTo(pr.x, pr.y);
      }
      ctx.stroke();

      // Degree Labels
      ctx.font = 'bold 11px JetBrains Mono';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.textAlign = 'center';
      for (let d = 0; d < 360; d += 45) {
        const theta = zeroHeading - (d * Math.PI / 180);
        const px = assetGeometry.centerX + assetGeometry.radius * 1.35 * Math.cos(theta), py = assetGeometry.centerY + assetGeometry.radius * 1.35 * Math.sin(theta), pr = project(px, py, focus.z);
        ctx.fillText(`${d}°`, pr.x, pr.y);
      }

      if (Math.abs((angle.pitch % 180) - 90) <= 45) {
        const gaugeX = assetGeometry.centerX - assetGeometry.radius * 1.5, gaugeY = assetGeometry.centerY;
        ctx.beginPath(); ctx.strokeStyle = 'rgba(6, 182, 212, 0.6)'; ctx.lineWidth = 3;
        const pB = project(gaugeX, gaugeY, assetGeometry.minZ), pT = project(gaugeX, gaugeY, assetGeometry.maxZ);
        ctx.moveTo(pB.x, pB.y); ctx.lineTo(pT.x, pT.y); ctx.stroke();
        ctx.fillStyle = 'rgba(6, 182, 212, 0.9)'; ctx.font = 'bold 12px JetBrains Mono';
        for (let z = Math.floor(assetGeometry.minZ); z <= Math.ceil(assetGeometry.maxZ); z++) {
          const p = project(gaugeX, gaugeY, z);
          ctx.beginPath(); ctx.lineWidth = 2; ctx.moveTo(p.x - 6, p.y); ctx.lineTo(p.x + 6, p.y); ctx.stroke();
          ctx.fillText(`${z}m`, p.x - 22, p.y + 4);
        }
      }

    }

    const axLen = assetGeometry.radius * 1.5;
    const orgPos = { x: assetGeometry.centerX, y: assetGeometry.centerY, z: floorZ };
    const origin = project(orgPos.x, orgPos.y, orgPos.z);
    const xEnd = project(orgPos.x + axLen, orgPos.y, orgPos.z);
    const yEnd = project(orgPos.x, orgPos.y + axLen, orgPos.z);
    const zEnd = project(orgPos.x, orgPos.y, orgPos.z + axLen);

    ctx.lineWidth = 4;
    ctx.beginPath(); ctx.strokeStyle = '#ef4444'; ctx.moveTo(origin.x, origin.y); ctx.lineTo(xEnd.x, xEnd.y); ctx.stroke();
    ctx.fillStyle = '#ef4444'; ctx.font = 'bold 24px Arial'; ctx.fillText('X', xEnd.x + 15, xEnd.y + 10);
    ctx.beginPath(); ctx.strokeStyle = '#22c55e'; ctx.moveTo(origin.x, origin.y); ctx.lineTo(yEnd.x, yEnd.y); ctx.stroke();
    ctx.fillStyle = '#22c55e'; ctx.fillText('Y', yEnd.x + 15, yEnd.y + 10);
    ctx.beginPath(); ctx.strokeStyle = '#3b82f6'; ctx.moveTo(origin.x, origin.y); ctx.lineTo(zEnd.x, zEnd.y); ctx.stroke();
    ctx.fillStyle = '#3b82f6'; ctx.fillText('Z', zEnd.x + 15, zEnd.y + 10);
  }, [points, findings, assetGeometry, getOpticalDistanceToCloud, zeroHeading, floorZ, previewMode, minimal, captureIndices, activeCaptureIndex, showCapOnly, hideFrustrums, range, logStart, baseTimestamp, displayTime]);

  const render = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const dpr = window.devicePixelRatio || 1, rect = canvas.getBoundingClientRect();
    if (canvas.width !== Math.round(rect.width * dpr) || canvas.height !== Math.round(rect.height * dpr)) { canvas.width = Math.round(rect.width * dpr); canvas.height = Math.round(rect.height * dpr); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.scale(dpr, dpr); }
    if (!isDraggingRef.current && (Math.abs(velocityRef.current.pitch) > 0.01 || Math.abs(velocityRef.current.yaw) > 0.01)) { setViewAngle(p => ({ pitch: p.pitch + velocityRef.current.pitch, yaw: p.yaw + velocityRef.current.yaw })); velocityRef.current.pitch *= 0.94; velocityRef.current.yaw *= 0.94; }

    drawMap(ctx, rect.width, rect.height, viewAngle, zoom, focusCoord || viewFocus, {
      hideFrustrums,
      showPath: previewMode !== 'MODEL',
      showModel: previewMode === 'BOTH' || previewMode === 'MODEL',
      minimal,
      showGuides: false
    });
  }, [drawMap, viewAngle, zoom, focusCoord, viewFocus, hideFrustrums, previewMode, minimal, pointDensity]);

  useEffect(() => { requestRef.current = requestAnimationFrame(function frame() { render(); requestRef.current = requestAnimationFrame(frame); }); return () => { if (requestRef.current) cancelAnimationFrame(requestRef.current); }; }, [render]);

  return (
    <div className="relative w-full h-full bg-[#020617] overflow-hidden shadow-sm border border-slate-400">
      <canvas
        ref={canvasRef}
        onContextMenu={(e) => e.preventDefault()}
        onMouseDown={(e) => {
          isDraggingRef.current = true;
          lastMousePosRef.current = { x: e.clientX, y: e.clientY };
          velocityRef.current = { pitch: 0, yaw: 0 };
          (e.currentTarget as any)._dragButton = e.button;
        }}
        onMouseMove={(e) => {
          if (!isDraggingRef.current) return;
          const dx = e.clientX - lastMousePosRef.current.x, dy = e.clientY - lastMousePosRef.current.y;
          const button = (e.currentTarget as any)._dragButton;

          if (button === 2) {
            // RIGHT CLICK -> PAN
            const cvs = canvasRef.current;
            if (cvs) {
              const baseScale = Math.min(cvs.width, cvs.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;
              const rY = (viewAngle.yaw * Math.PI) / 180, rP = (viewAngle.pitch * Math.PI) / 180;
              const cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.sin(rP);

              const dCx = (dx * -cosY + dy * -sinY * cosP) / baseScale;
              const dCy = (dx * sinY + dy * -cosY * cosP) / baseScale;
              const dCz = (dy * sinP) / baseScale;

              setFocusCoord(p => {
                const base = p || viewFocus;
                // Reversing the pan mapping so map moves with cursor natively (Z is inverted)
                return { x: base.x - dCx, y: base.y - dCy, z: base.z + dCz };
              });
            }
          } else {
            // LEFT CLICK -> ORBIT
            velocityRef.current = { yaw: dx * 0.4, pitch: dy * 0.4 };
            setViewAngle(p => ({ yaw: p.yaw + dx * 0.4, pitch: p.pitch + dy * 0.4 }));
          }
          lastMousePosRef.current = { x: e.clientX, y: e.clientY };
        }}
        onMouseUp={() => isDraggingRef.current = false}
        onWheel={(e) => setZoom(p => Math.min(Math.max(p * (e.deltaY > 0 ? 0.92 : 1.08), 0.1), 10))}
        className="w-full h-full cursor-grab"
      />
      <div className="absolute top-4 right-4 flex items-center gap-2 bg-slate-300/90 backdrop-blur-md p-1 border border-slate-400 z-50 shadow-sm">
        <button onClick={() => setView('Side')} className="px-3 py-1.5 text-[9px] font-bold bg-slate-300 text-slate-800 hover:bg-slate-300 hover:text-[#0000a9] transition uppercase">Side</button>
        <button onClick={() => setView('Front')} className="px-3 py-1.5 text-[9px] font-bold bg-slate-300 text-slate-800 hover:bg-slate-300 hover:text-[#0000a9] transition uppercase">Front</button>
        <button onClick={() => setView('Top')} className="px-3 py-1.5 text-[9px] font-bold bg-slate-300 text-slate-800 hover:bg-slate-300 hover:text-[#0000a9] transition uppercase">Top</button>
        <div className="flex items-center gap-2 px-2 border-l border-slate-400">
          <span className="text-[9px] font-black text-slate-700 uppercase tracking-widest whitespace-nowrap">PTS</span>
          <input
            type="range" min={1} max={100} value={pointDensity}
            onChange={e => setPointDensity(Number(e.target.value))}
            className="w-20 h-1 accent-[#0000a9] cursor-pointer"
          />
          <span className="text-[9px] font-mono text-slate-700 w-6 text-right">{pointDensity}</span>
        </div>
      </div>
      {!minimal && activeFootprint && (
        <div className="absolute top-6 left-6 flex flex-col gap-3 z-50 pointer-events-none">
          <div className="bg-slate-300/90 backdrop-blur-md px-5 py-4 border border-slate-400 text-left shadow-lg min-w-[160px] group pointer-events-auto transition-all hover:bg-slate-300"><span className="text-[9px] text-slate-500 font-black uppercase block mb-1">Standoff (D)</span><span className="text-base font-mono font-black text-slate-900">{activeFootprint.centerStandoffCm} cm</span></div>
          <div className="bg-slate-300/90 backdrop-blur-md px-5 py-4 border border-slate-400 text-left shadow-lg min-w-[160px] group pointer-events-auto transition-all hover:bg-slate-300"><span className="text-[9px] text-slate-500 font-black uppercase block mb-1">Surface View</span><span className="text-base font-mono font-black text-slate-800">{activeFootprint.widthCm}x{activeFootprint.heightCm} cm</span></div>
        </div>
      )}
      <div className="absolute bottom-4 right-4 text-[8px] font-mono text-slate-800 uppercase tracking-widest bg-black/40 px-2 py-1">V6.0 NEURAL_MAP_T_CORE</div>
    </div>
  );
};
